import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import IngredientAPI from '../api/ingredient.js';

/**
 * Hook for managing ingredient-based product search.
 * Handles API calls with timeout, loading states, error handling, and retry functionality.
 * 
 * @param {string[]} ingredients - Array of ingredient URIs to search for
 * @param {'and'|'or'} mode - Combination mode: 'and' for all ingredients, 'or' for any ingredient
 * @param {number} page - Page number for pagination (1-indexed)
 * @returns {Object} Search state and controls
 */
export function useIngredientSearch(ingredients = [], mode = 'and', page = 1) {
  // Stabilize ingredients array reference to prevent infinite re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableIngredients = useMemo(() => ingredients, [JSON.stringify(ingredients)]);

  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalItems: 0,
    totalPages: 0
  });
  const [isLoading, setIsLoading] = useState(() => stableIngredients.length > 0);
  const [error, setError] = useState(null);
  
  // Store current search params for retry functionality
  const searchParamsRef = useRef({ ingredients: stableIngredients, mode, page });
  
  // Track mounted state to prevent state updates after unmount
  const isMountedRef = useRef(true);
  
  // Track abort controller for cleanup
  const abortControllerRef = useRef(null);

  // Update search params ref when inputs change
  useEffect(() => {
    searchParamsRef.current = { ingredients: stableIngredients, mode, page };
  }, [stableIngredients, mode, page]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Execute search when parameters change
  useEffect(() => {
    // Don't search if no ingredients selected
    if (!stableIngredients || stableIngredients.length === 0) {
      setResults([]);
      setPagination({ page: 1, totalItems: 0, totalPages: 0 });
      setError(null);
      setIsLoading(false);
      return;
    }

    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    setIsLoading(true);
    setError(null);

    const timeoutId = setTimeout(() => controller.abort(), 15000);

    IngredientAPI.searchByIngredients(
      stableIngredients,
      mode,
      page,
      { signal: controller.signal }
    )
      .then((data) => {
        clearTimeout(timeoutId);
        if (isMountedRef.current && !controller.signal.aborted) {
          if (!data) {
            // catchAsync swallows errors and returns undefined
            setError('An error occurred while searching. Please try again.');
            setResults([]);
            setPagination({ page: page, totalItems: 0, totalPages: 0 });
            setIsLoading(false);
            return;
          }
          setResults(data.products || []);
          setPagination(data.pagination || {
            page: page,
            totalItems: 0,
            totalPages: 0
          });
          setError(null);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (isMountedRef.current && !controller.signal.aborted) {
          // Handle timeout specifically (Req 7.2)
          if (err.name === 'AbortError') {
            setError('Request timed out. Please try again.');
          } else {
            // Handle other API errors (Req 7.3)
            setError(err.message || 'An error occurred while searching. Please try again.');
          }
          setResults([]);
          setPagination({ page: page, totalItems: 0, totalPages: 0 });
          setIsLoading(false);
        }
      });

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [stableIngredients, mode, page]);

  /**
   * Retry the current search query (Req 7.4).
   * Re-executes the search with the same parameters.
   */
  const retry = useCallback(() => {
    const { ingredients: uris, mode: searchMode, page: searchPage } = searchParamsRef.current;
    
    // Don't retry if no ingredients
    if (!uris || uris.length === 0) {
      return;
    }

    // Abort any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    setIsLoading(true);
    setError(null);

    const timeoutId = setTimeout(() => controller.abort(), 15000);

    IngredientAPI.searchByIngredients(
      uris,
      searchMode,
      searchPage,
      { signal: controller.signal }
    )
      .then((data) => {
        clearTimeout(timeoutId);
        if (isMountedRef.current && !controller.signal.aborted) {
          if (!data) {
            setError('An error occurred while searching. Please try again.');
            setResults([]);
            setPagination({ page: searchPage, totalItems: 0, totalPages: 0 });
            setIsLoading(false);
            return;
          }
          setResults(data.products || []);
          setPagination(data.pagination || {
            page: searchPage,
            totalItems: 0,
            totalPages: 0
          });
          setError(null);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (isMountedRef.current && !controller.signal.aborted) {
          // Handle timeout specifically (Req 7.2)
          if (err.name === 'AbortError') {
            setError('Request timed out. Please try again.');
          } else {
            // Handle other API errors (Req 7.3)
            setError(err.message || 'An error occurred while searching. Please try again.');
          }
          setResults([]);
          setPagination({ page: searchPage, totalItems: 0, totalPages: 0 });
          setIsLoading(false);
        }
      });
  }, []);

  return {
    results,
    pagination,
    isLoading,
    error,
    retry
  };
}

export default useIngredientSearch;
